import { useEffect, useState } from 'react';
import {
  downloadPrivateFile,
  getFilePreviewUrl,
  getPrivateFileUrl,
  viewPrivateFile,
} from '@/shared/lib/utils/privateFiles';
import type { ChatAttachment } from '../model/types';

/**
 * Вложения чата — приватные файлы: превью картинки приходит в url, остальное открывается по
 * ключу. У неотправленного сообщения вложение локальное — url это blob.
 */

export function getChatAttachmentPreviewUrl(attachment: ChatAttachment): string | null {
  return attachment.isLocal ? attachment.url : getFilePreviewUrl(attachment);
}

export function openChatAttachment(attachment: ChatAttachment): void {
  if (attachment.isLocal) {
    if (attachment.url) window.open(attachment.url, '_blank', 'noopener,noreferrer');
    return;
  }
  void viewPrivateFile(attachment);
}

export function downloadChatAttachment(attachment: ChatAttachment): void {
  if (attachment.isLocal) {
    if (!attachment.url) return;
    const link = document.createElement('a');
    link.href = attachment.url;
    link.download = attachment.name;
    link.click();
    return;
  }
  void downloadPrivateFile(attachment);
}

/** Ссылка на файл для плеера/просмотрщика: из ответа или запросом по ключу, когда enabled. */
export function useChatAttachmentMediaUrl(attachment: ChatAttachment | null, enabled = true) {
  // Ссылка, которая есть сразу: локальный blob или свежий url картинки из ответа
  const immediateUrl = attachment && enabled ? getChatAttachmentPreviewUrl(attachment) : null;
  const [fetched, setFetched] = useState<{ key: string; url: string | null } | null>(null);

  useEffect(() => {
    if (!attachment || !enabled || attachment.isLocal || immediateUrl) return;
    let cancelled = false;
    getPrivateFileUrl(attachment)
      .then((url) => !cancelled && setFetched({ key: attachment.key, url }))
      .catch(() => !cancelled && setFetched({ key: attachment.key, url: null }));
    return () => {
      cancelled = true;
    };
  }, [attachment, enabled, immediateUrl]);

  const fetchedForThis = fetched && attachment && fetched.key === attachment.key ? fetched : null;
  const url = immediateUrl ?? fetchedForThis?.url ?? null;
  const failed = Boolean(attachment && enabled && !url && (attachment.isLocal || fetchedForThis));
  return { url, failed };
}
